import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  createUniver,
  defaultTheme,
  LocaleType,
  merge,
  type FUniver,
} from "@univerjs/presets";
import { UniverSheetsCorePreset } from "@univerjs/presets/preset-sheets-core";
import UniverPresetSheetsCoreEnUS from "@univerjs/presets/preset-sheets-core/locales/en-US";
import "@univerjs/presets/lib/styles/preset-sheets-core.css";
import * as XLSX from "xlsx";
import { Button } from "../primitives/button";
import { AiCellPopover, type AiCellPopoverProps } from "./AiCellPopover";
import {
  SHEET_CHART_KEY,
  SheetChartPopover,
  type SheetChartPopoverProps,
} from "./SheetChartPopover";
import { parseXlsxCharts } from "./xlsxChartParse";
import type { EditSession } from "./EditSession";
import { fetchSessionBytes } from "./fetchSessionBytes";
import { type AIProposal, saveDraft } from "./persistence";

/** Univer canvas-popup component key — registered once per Univer instance
 *  via ``univerAPI.registerComponent``. Stable string so we can attach +
 *  detach popups across the lifetime of the editor without re-registering. */
const AI_CELL_POPOVER_KEY = "AndoAiCellPopover";

/**
 * One AI-modified cell tracked locally so the review panel can render an
 * accept/reject affordance. Univer community has no native tracked-changes
 * layer for sheets, so the editor synthesises one: on every
 * `applyAIProposal()` we capture the LIVE cell value before overwriting it
 * and stash the pair here. Accept clears the highlight; reject restores
 * the captured `valueBefore`.
 */
export interface XlsxAIChange {
  /** Stable id used by the review panel as React key + accept/reject discriminator. */
  readonly id: string;
  readonly sheet: string;
  readonly row: number;
  readonly col: number;
  readonly valueBefore: string | number | boolean | null | undefined;
  readonly valueAfter: string | number | boolean | null | undefined;
  /** Originating proposal_id for the `proposalsStore` cross-ref. */
  readonly proposalId: string | null;
}

/**
 * Imperative handle the chat HITL bridge holds so it can push AI cell edits
 * without reaching into Univer's facade. Mirrors {@link DocxEditorInlineHandle}
 * — the bridge stays mime-agnostic and routes on `proposal.kind`. Plus a
 * second surface for the right-rail review panel that wraps the locally-
 * tracked AI cell map (xlsx tracked-changes synth — see {@link XlsxAIChange}).
 */
export interface XlsxEditorInlineHandle {
  /** Applies a single tracked cell edit. Returns false on unsupported kind. */
  applyAIProposal: (proposal: AIProposal) => boolean;
  /** Snapshots current bytes + invokes upstream onSave. */
  flush: () => Promise<void>;
  // ── Review-panel surface ────────────────────────────────────────────────
  /** Snapshot every AI-modified cell currently awaiting review. */
  listAIChanges: () => readonly XlsxAIChange[];
  /** Accept a single AI change by id: drop the highlight, keep the new value. */
  acceptCellAt: (id: string) => boolean;
  /** Reject a single AI change by id: restore valueBefore + drop the highlight. */
  rejectCellAt: (id: string) => boolean;
  /** Accept every pending AI change in the workbook. */
  acceptAllAICells: () => boolean;
  /** Reject every pending AI change. Restores values in insertion order. */
  rejectAllAICells: () => boolean;
  /** Scroll the workbook so the cell of the given AI change id is visible. */
  scrollToAICell: (id: string) => void;
  /** Subscribe to the AI change map mutations (apply/accept/reject). */
  subscribeAIChanges: (listener: () => void) => () => void;
  /** OfficeCLI path of the active cell (e.g. `/Ricavi/B5`), or null if no
   *  single-cell selection. Drives selection-aware region-edit so a rewrite
   *  targets the EXACT cell instead of a fragile whole-doc find/replace. */
  getSelectedPath: () => string | null;
}

export interface XlsxEditorInlineProps {
  session: EditSession;
  onSave?: (blob: Blob) => Promise<void>;
}

const DRAFT_DEBOUNCE_MS = 800;

/** Highlight colour for AI-suggested cells. Matches `text-ando-orange` token. */
const AI_PROPOSAL_BG = "#FFE6CC";

/** Canonical XLSX MIME — kept here so onSave consumers don't reinvent it. */
const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Default grid size when the worksheet has no `!ref` (truly empty). Picked
 *  to feel "spreadsheet-shaped" without bloating the snapshot. */
const DEFAULT_ROW_COUNT = 100;
const DEFAULT_COLUMN_COUNT = 26;

/** Univer cell-type discriminator (matches `CellValueType` enum in
 *  `@univerjs/core` — STRING=1, NUMBER=2, BOOLEAN=3, FORCE_STRING=4). We
 *  hard-code the numeric values instead of importing the enum so this
 *  module stays loosely coupled to Univer's deep type tree. */
const UNIVER_CELL_TYPE_STRING = 1;
const UNIVER_CELL_TYPE_NUMBER = 2;
const UNIVER_CELL_TYPE_BOOLEAN = 3;

/** Shape of a Univer worksheet snapshot we hand to `createWorkbook`. We
 *  type it loosely (Partial-ish) because we only populate the fields
 *  Univer actually needs to render values; the engine fills in the rest. */
interface UniverSheetSnapshot {
  id: string;
  name: string;
  rowCount: number;
  columnCount: number;
  cellData: {
    [row: number]: {
      [col: number]: {
        v?: string | number | boolean | null;
        t?: number;
        f?: string;
      };
    };
  };
}

interface UniverWorkbookSnapshot {
  id: string;
  name: string;
  appVersion: string;
  locale: LocaleType;
  styles: Record<string, unknown>;
  sheetOrder: string[];
  sheets: { [sheetId: string]: UniverSheetSnapshot };
}

/**
 * Translate a SheetJS `WorkBook` into a Univer workbook snapshot.
 *
 * Why we do this hand-roll instead of relying on Univer's exchange plugin:
 * `@univerjs/preset-sheets-core` ships *no* XLSX importer (the importer
 * lives in `@univerjs-pro/exchange-client`, paid). We use SheetJS (MIT)
 * to parse the .xlsx bytes into a plain JS workbook, then walk every
 * sheet's `!ref` range and copy each cell's value + type into the
 * Univer snapshot shape declared in `@univerjs/core/types/sheets`.
 *
 * Styles are intentionally NOT ported in this first pass — the user's
 * complaint was "nessuna riga visibile". Value visibility unlocks the
 * fix; style fidelity is a follow-up.
 */
function workbookToUniverSnapshot(
  wb: XLSX.WorkBook,
  unitId: string,
  unitName: string,
): UniverWorkbookSnapshot {
  const sheetOrder: string[] = [];
  const sheets: { [sheetId: string]: UniverSheetSnapshot } = {};

  const sheetNames =
    wb.SheetNames && wb.SheetNames.length > 0 ? wb.SheetNames : ["Sheet1"];

  for (let i = 0; i < sheetNames.length; i += 1) {
    // `noUncheckedIndexedAccess` makes positional access nullable; fall
    // back to a synthetic name so we never feed `undefined` to lookups.
    const sheetName = sheetNames[i] ?? `Sheet${i + 1}`;
    const ws = wb.Sheets[sheetName];
    const sheetId = `sheet-${i + 1}`;
    sheetOrder.push(sheetId);

    const cellData: UniverSheetSnapshot["cellData"] = {};
    let rowCount = DEFAULT_ROW_COUNT;
    let columnCount = DEFAULT_COLUMN_COUNT;

    if (ws?.["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"] as string);
      rowCount = Math.max(rowCount, range.e.r + 1);
      columnCount = Math.max(columnCount, range.e.c + 1);

      for (let r = range.s.r; r <= range.e.r; r += 1) {
        for (let c = range.s.c; c <= range.e.c; c += 1) {
          const addr = XLSX.utils.encode_cell({ r, c });
          const cell = ws[addr] as XLSX.CellObject | undefined;
          if (!cell) continue;
          // SheetJS cell types: n=number, s=string, b=bool, d=date,
          // e=error, z=stub. We collapse dates to ISO strings (Univer
          // OSS has no native date cell type), errors to their string
          // representation, and stubs to null.
          let value: string | number | boolean | null = null;
          let univerType: number | undefined;
          switch (cell.t) {
            case "n":
              value = typeof cell.v === "number" ? cell.v : Number(cell.v ?? 0);
              univerType = UNIVER_CELL_TYPE_NUMBER;
              break;
            case "s":
              value = cell.v == null ? "" : String(cell.v);
              univerType = UNIVER_CELL_TYPE_STRING;
              break;
            case "b":
              value = Boolean(cell.v);
              univerType = UNIVER_CELL_TYPE_BOOLEAN;
              break;
            case "d":
              value =
                cell.v instanceof Date
                  ? cell.v.toISOString()
                  : String(cell.v ?? "");
              univerType = UNIVER_CELL_TYPE_STRING;
              break;
            case "e":
              value = cell.w ?? String(cell.v ?? "");
              univerType = UNIVER_CELL_TYPE_STRING;
              break;
            default:
              // "z" stubs + anything unrecognised: fall back to the
              // formatted text if present, else the raw value.
              if (cell.w != null) {
                value = cell.w;
                univerType = UNIVER_CELL_TYPE_STRING;
              } else if (cell.v != null) {
                value =
                  typeof cell.v === "number" ||
                  typeof cell.v === "boolean" ||
                  typeof cell.v === "string"
                    ? cell.v
                    : String(cell.v);
                univerType =
                  typeof cell.v === "number"
                    ? UNIVER_CELL_TYPE_NUMBER
                    : typeof cell.v === "boolean"
                      ? UNIVER_CELL_TYPE_BOOLEAN
                      : UNIVER_CELL_TYPE_STRING;
              }
          }
          const rowBag = cellData[r] ?? (cellData[r] = {});
          rowBag[c] = {
            v: value,
            t: univerType,
            // Preserve original formula text — Univer's OSS engine will
            // re-evaluate on edit; on round-trip back to XLSX we re-emit
            // the formula so the BE-stored file stays canonical.
            // SheetJS exposes ``cell.f`` WITHOUT the leading ``=`` (its
            // convention), but Univer's ``f`` field expects the canonical
            // ``=``-prefixed form — without it the formula bar shows
            // "MEDIAN(D2:D15)" instead of "=MEDIAN(D2:D15)" (Jun 24, 2026,
            // Fabio's Excel). Prepend ``=`` idempotently.
            ...(cell.f
              ? { f: cell.f.startsWith("=") ? cell.f : `=${cell.f}` }
              : {}),
          };
        }
      }
    }

    sheets[sheetId] = {
      id: sheetId,
      name: sheetName,
      rowCount,
      columnCount,
      cellData,
    };
  }

  return {
    id: unitId,
    name: unitName,
    appVersion: "0.23.0",
    locale: LocaleType.EN_US,
    styles: {},
    sheetOrder,
    sheets,
  };
}

/**
 * Reverse of {@link workbookToUniverSnapshot}: walks the Univer snapshot,
 * builds a SheetJS WorkBook, returns serialised XLSX bytes.
 *
 * Round-trip caveat: only values + formulas survive the OSS path —
 * styles, comments, charts, pivot tables, validations are dropped on
 * save. That matches what the OSS Univer engine can hold; richer round-
 * trip is gated on the Univer-Pro exchange plugin.
 */
function univerSnapshotToXlsxBytes(snapshot: UniverWorkbookSnapshot): Uint8Array {
  const wb = XLSX.utils.book_new();

  for (const sheetId of snapshot.sheetOrder) {
    const sheet = snapshot.sheets[sheetId];
    if (!sheet) continue;
    const ws: XLSX.WorkSheet = {};
    let maxRow = -1;
    let maxCol = -1;

    const cellData = sheet.cellData ?? {};
    const rowKeys = Object.keys(cellData);
    for (const rk of rowKeys) {
      const r = Number(rk);
      if (!Number.isFinite(r)) continue;
      const rowBag = cellData[r];
      if (!rowBag) continue;
      const colKeys = Object.keys(rowBag);
      for (const ck of colKeys) {
        const c = Number(ck);
        if (!Number.isFinite(c)) continue;
        const cell = rowBag[c];
        if (!cell) continue;
        const v = cell.v;
        const f = cell.f;
        if (v == null && !f) continue;
        const addr = XLSX.utils.encode_cell({ r, c });
        let outCell: XLSX.CellObject;
        if (typeof v === "number") {
          outCell = { t: "n", v };
        } else if (typeof v === "boolean") {
          outCell = { t: "b", v };
        } else if (v == null) {
          outCell = { t: "z", v: null as unknown as undefined };
        } else {
          outCell = { t: "s", v: String(v) };
        }
        // SheetJS expects the formula WITHOUT a leading ``=`` (mirror of the
        // import prepend) — keep the saved .xlsx canonical so the BE binary
        // re-reads real formulas, not "=..."-as-text.
        if (f) outCell.f = f.startsWith("=") ? f.slice(1) : f;
        ws[addr] = outCell;
        if (r > maxRow) maxRow = r;
        if (c > maxCol) maxCol = c;
      }
    }

    // `!ref` is required by SheetJS writers — without it the sheet
    // serialises as completely empty even with populated cells.
    if (maxRow >= 0 && maxCol >= 0) {
      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: maxRow, c: maxCol },
      });
    } else {
      ws["!ref"] = "A1";
    }

    XLSX.utils.book_append_sheet(wb, ws, sheet.name || sheetId);
  }

  // If the user wiped every sheet we still need ONE sheet for a valid
  // .xlsx — match Excel's "Sheet1" default.
  if (wb.SheetNames.length === 0) {
    XLSX.utils.book_append_sheet(wb, { "!ref": "A1" }, "Sheet1");
  }

  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return out as Uint8Array;
}

/**
 * XlsxEditorInline — Univer Sheets host with local-first persistence.
 *
 * Why Univer (vs Luckysheet / x-spreadsheet / Handsontable)? Univer is the
 * only OSS sheet engine that ships a Facade API stable enough to drive
 * programmatic AI mutations (set value + style + comment) without forking
 * the core. The trade-off is the `preset-sheets-core` open-source bundle
 * does NOT include XLSX import/export — that's the paid
 * `@univerjs-pro/exchange-client` plugin. We work around it by parsing
 * the bytes with SheetJS (MIT) on import and re-serialising on save —
 * see {@link workbookToUniverSnapshot} / {@link univerSnapshotToXlsxBytes}.
 *
 * The component owns the Univer lifecycle: it instantiates the engine on
 * mount, fetches + parses the `read_url` bytes, hands the resulting
 * snapshot to `createWorkbook`, and `dispose()`s on unmount so a session
 * swap doesn't leak two canvases on the page.
 */
export const XlsxEditorInline = forwardRef<
  XlsxEditorInlineHandle,
  XlsxEditorInlineProps
>(function XlsxEditorInline({ session, onSave }, ref) {
  const { i18n } = useLingui();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const univerRef = useRef<{
    univer: { dispose: () => void };
    univerAPI: FUniver;
  } | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Boot Univer once + load the workbook from `session.read_url`. We
  // deliberately don't re-instantiate on every session change — a swap
  // dispatches a new workbook into the same engine below.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const ctrl = new AbortController();
    let cancelled = false;

    (async () => {
      let snapshot: UniverWorkbookSnapshot;
      // Hoisted so the native-chart render (below, after Univer mounts) can
      // re-parse charts from the SAME bytes Univer dropped on import.
      let bytes: Awaited<ReturnType<typeof fetchSessionBytes>> | undefined;
      try {
        bytes = await fetchSessionBytes(session.read_url, {
          signal: ctrl.signal,
        });
        const wb = XLSX.read(bytes, {
          type: "array",
          cellStyles: true,
          cellFormula: true,
        });
        snapshot = workbookToUniverSnapshot(
          wb,
          session.session_id,
          session.filename,
        );
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        // Defensive: even when parse fails we still mount Univer with an
        // empty workbook + surface the error banner, so the user sees
        // SOMETHING and can retry.
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        snapshot = {
          id: session.session_id,
          name: session.filename,
          appVersion: "0.23.0",
          locale: LocaleType.EN_US,
          styles: {},
          sheetOrder: ["sheet-1"],
          sheets: {
            "sheet-1": {
              id: "sheet-1",
              name: "Sheet1",
              rowCount: DEFAULT_ROW_COUNT,
              columnCount: DEFAULT_COLUMN_COUNT,
              cellData: {},
            },
          },
        };
      }

      if (cancelled) return;

      try {
        const instance = createUniver({
          locale: LocaleType.EN_US,
          // Locale strings for every plugin Univer auto-mounts (sheets-core
          // includes HyperlinkDialog, FindReplace, etc. — each requires its
          // strings registered or the dialog throws "Locale not initialized"
          // on first hover/click). May 24, 2026 bug fix.
          locales: {
            [LocaleType.EN_US]: merge({}, UniverPresetSheetsCoreEnUS),
          },
          theme: defaultTheme,
          presets: [UniverSheetsCorePreset({ container: host })],
        });
        univerRef.current = instance;
        // `createWorkbook` accepts `Partial<IWorkbookData>`. The snapshot
        // shape mirrors IWorkbookData; the cast keeps this module loose
        // on Univer's deep type tree.
        instance.univerAPI.createWorkbook(
          snapshot as unknown as Parameters<
            FUniver["createWorkbook"]
          >[0],
        );
        // Register the AI cell popover component once per Univer instance.
        // Univer's renderer reads the registered key when ``attachPopup``
        // is called (May 25, 2026 SOTA: canvas-pop scroll-tracked free).
        if (!popoverRegisteredRef.current) {
          try {
            (instance.univerAPI as unknown as {
              registerComponent: (
                key: string,
                comp: React.ComponentType<AiCellPopoverProps>,
              ) => void;
            }).registerComponent(AI_CELL_POPOVER_KEY, AiCellPopover);
            // Native-chart renderer (Univer OSS has no chart plugin) — same
            // canvas-popup mechanism, anchored on the sheet. See attach below.
            (instance.univerAPI as unknown as {
              registerComponent: (
                key: string,
                comp: React.ComponentType<SheetChartPopoverProps>,
              ) => void;
            }).registerComponent(SHEET_CHART_KEY, SheetChartPopover);
            popoverRegisteredRef.current = true;
          } catch (regErr) {
            console.warn(
              "[XlsxEditorInline] registerComponent failed (popover disabled)",
              regErr,
            );
          }
        }
        // NATIVE CHART RENDER (Jun 24, 2026): Univer OSS drops native xlsx
        // charts on import. Parse them from the bytes + re-draw with ECharts
        // anchored at each chart's host-sheet cell, so the in-app editor shows
        // what Excel/download show. Best-effort + non-blocking: a chart parse
        // failure never stops the sheet from mounting.
        try {
          const charts = bytes ? await parseXlsxCharts(bytes) : [];
          if (!cancelled && charts.length) {
            const api = instance.univerAPI;
            const workbook = api.getActiveWorkbook?.();
            for (const spec of charts) {
              try {
                const sheetObj =
                  (spec.sheetName && workbook?.getSheetByName(spec.sheetName)) ||
                  workbook?.getActiveSheet();
                if (!sheetObj) continue;
                const range = sheetObj.getRange(spec.row, spec.col);
                const disposable = (
                  range as unknown as {
                    attachPopup: (popup: {
                      componentKey: string;
                      direction?: "horizontal" | "vertical";
                      hideOnInvisible?: boolean;
                      extraProps?: Record<string, unknown>;
                    }) => { dispose(): void } | null;
                  }
                ).attachPopup({
                  componentKey: SHEET_CHART_KEY,
                  direction: "vertical",
                  hideOnInvisible: true,
                  extraProps: { spec },
                });
                if (disposable) {
                  popoverDisposablesRef.current.set(
                    `chart:${spec.sheetName}:${spec.row}:${spec.col}`,
                    disposable,
                  );
                }
              } catch (chErr) {
                console.warn("[XlsxEditorInline] chart attach failed", chErr);
              }
            }
          }
        } catch (chErr) {
          console.warn("[XlsxEditorInline] parseXlsxCharts failed", chErr);
        }
        if (!cancelled) setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
      // Dispose every cell popover BEFORE tearing down Univer — Univer's
      // own dispose() clears its popup service, but we keep our local
      // Map clean to avoid stale disposables surviving a hot-reload.
      popoverDisposablesRef.current.forEach((d) => {
        try {
          d?.dispose();
        } catch {
          /* idempotent */
        }
      });
      popoverDisposablesRef.current.clear();
      popoverRegisteredRef.current = false;
      univerRef.current?.univer.dispose();
      univerRef.current = null;
      setReady(false);
    };
    // We intentionally only boot once per mount — session.* changes drive
    // re-imports below, not full disposal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Pull a JSON snapshot of the active workbook, serialise to .xlsx
   *  bytes, persist locally as a draft, and return the Blob so the
   *  caller (flush) can hand it to the BE.
   *
   *  The local-draft Blob is the SAME bytes the BE will receive — keeps
   *  recovery deterministic: a restored draft round-trips byte-identical
   *  to what was last on disk. */
  const persistDraft = useCallback(async (): Promise<Blob | null> => {
    const api = univerRef.current?.univerAPI;
    if (!api) return null;
    const workbook = api.getActiveWorkbook();
    if (!workbook) return null;
    const snapshot = workbook.getSnapshot() as unknown as UniverWorkbookSnapshot;
    let bytes: Uint8Array;
    try {
      bytes = univerSnapshotToXlsxBytes(snapshot);
    } catch (err) {
      console.warn("[XlsxEditorInline] failed to serialise XLSX", err);
      return null;
    }
    // Pass a fresh ArrayBuffer copy to the Blob ctor — TS's DOM lib types
    // BlobPart as ArrayBufferView<ArrayBuffer> (NOT ArrayBufferLike), so
    // a Uint8Array whose underlying buffer is union-typed with
    // SharedArrayBuffer (the default since TS 5.7) is rejected. Copying
    // into a plain ArrayBuffer satisfies the constraint with zero runtime
    // cost on hot path (XLSX serialisation already dominates).
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab], { type: XLSX_MIME });
    await saveDraft({
      session_id: session.session_id,
      doc_id: session.doc_id,
      mime_type: session.mime_type,
      blob,
      base_etag: session.etag,
      dirty_since: Date.now(),
      ai_proposals: [],
    });
    return blob;
  }, [
    session.session_id,
    session.doc_id,
    session.mime_type,
    session.etag,
  ]);

  const scheduleDraft = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void persistDraft();
    }, DRAFT_DEBOUNCE_MS);
  }, [persistDraft]);

  // Subscribe to Univer command stream so any edit (typed value, paste,
  // formula recalc) bumps the dirty flag + reschedules a draft snapshot.
  useEffect(() => {
    if (!ready) return;
    const api = univerRef.current?.univerAPI;
    if (!api) return;
    // `onCommandExecuted` fires on every committed mutation — equivalent to
    // the docx editor's onChange. We don't filter by command id (too many
    // to enumerate); debounce absorbs the noise.
    const disposable = api.onCommandExecuted(() => {
      setDirty(true);
      scheduleDraft();
    });
    return () => disposable.dispose();
  }, [ready, scheduleDraft]);

  const flush = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const blob = await persistDraft();
      if (blob && onSave) await onSave(blob);
      if (blob) {
        await saveDraft({
          session_id: session.session_id,
          doc_id: session.doc_id,
          mime_type: session.mime_type,
          blob,
          base_etag: session.etag,
          dirty_since: null,
          ai_proposals: [],
        });
      }
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, [
    onSave,
    persistDraft,
    saving,
    session.doc_id,
    session.etag,
    session.mime_type,
    session.session_id,
  ]);

  // ── AI tracked-changes synth (xlsx parity for the review panel) ─────────
  //
  // Univer community doesn't ship a tracked-changes layer for sheets, so
  // we synthesise one client-side: a Map keyed by an opaque id of every
  // cell the AI overwrote, holding the captured `valueBefore`. Accept
  // drops the highlight + map entry; reject restores `valueBefore` +
  // drops the entry. `subscribeAIChanges` notifies the review panel
  // whenever the map mutates so it can re-derive its list.
  const aiChangesRef = useRef<Map<string, XlsxAIChange>>(new Map());
  // Disposables for Univer canvas-popups anchored to each AI-edited cell.
  // Keyed by `XlsxAIChange.id` so accept/reject can dispose the matching
  // popover without scanning. Cleared in editor unmount (see Univer
  // dispose() block) — Univer's IDisposable.dispose() is idempotent so
  // double-dispose on accept-then-unmount is safe.
  const popoverDisposablesRef = useRef<
    Map<string, { dispose(): void } | undefined>
  >(new Map());
  // Track whether we've already registered the Univer component so we
  // don't re-register on hot-reload (Univer warns + ignores duplicates).
  const popoverRegisteredRef = useRef(false);
  const aiListenersRef = useRef<Set<() => void>>(new Set());
  const notifyAIChange = useCallback(() => {
    for (const listener of aiListenersRef.current) listener();
  }, []);

  const cellKey = useCallback((sheet: string, row: number, col: number, proposalId: string | null): string => {
    // Include proposalId so the SAME cell hit by two distinct AI proposals
    // (rare but legal) renders as two distinct review entries.
    return `xlsx:${sheet}:${row}:${col}:${proposalId ?? "anon"}`;
  }, []);

  /** Read the current Univer cell value (the value BEFORE we overwrite it). */
  const readCellValue = useCallback(
    (
      api: FUniver,
      sheetName: string | undefined,
      row: number,
      col: number,
    ): string | number | boolean | null | undefined => {
      const workbook = api.getActiveWorkbook();
      if (!workbook) return undefined;
      const sheet =
        (sheetName && workbook.getSheetByName(sheetName)) ||
        workbook.getActiveSheet();
      if (!sheet) return undefined;
      const range = sheet.getRange(row, col);
      const raw = range.getValue();
      if (raw === null || raw === undefined) return null;
      // Univer ranges return either a primitive (when reading a single
      // cell) or an `ICellData` ({v, t, f, s}). Normalise here.
      if (typeof raw === "object" && raw !== null && "v" in raw) {
        return (raw as { v?: string | number | boolean | null }).v ?? null;
      }
      return raw as string | number | boolean | null;
    },
    [],
  );

  /** Apply an AI change OR restore a pre-AI value. Highlight = orange iff
   *  `isAIWrite`. Returns true on success. */
  const writeCell = useCallback(
    (
      sheetName: string | undefined,
      row: number,
      col: number,
      value: string | number | boolean | null | undefined,
      isAIWrite: boolean,
    ): boolean => {
      const api = univerRef.current?.univerAPI;
      if (!api) return false;
      const workbook = api.getActiveWorkbook();
      if (!workbook) return false;
      const sheet =
        (sheetName && workbook.getSheetByName(sheetName)) ||
        workbook.getActiveSheet();
      if (!sheet) return false;
      const range = sheet.getRange(row, col);
      range.setValue(
        isAIWrite
          ? { v: value ?? null, s: { bg: { rgb: AI_PROPOSAL_BG } } }
          : { v: value ?? null, s: { bg: null } },
      );
      return true;
    },
    [],
  );

  // Accept / reject implementations lifted out of the imperative handle
  // so the Univer canvas-popover (rendered ABOVE the spreadsheet) can
  // call into the same logic via its ``onAccept``/``onReject`` extraProps.
  // Single source of truth — no divergence between in-rail accept vs
  // in-cell accept (May 25, 2026 SOTA pass).
  const disposePopover = useCallback((id: string) => {
    const d = popoverDisposablesRef.current.get(id);
    if (d) {
      try {
        d.dispose();
      } catch {
        /* idempotent */
      }
      popoverDisposablesRef.current.delete(id);
    }
  }, []);
  const acceptCellAtImpl = useCallback(
    (id: string): boolean => {
      const change = aiChangesRef.current.get(id);
      if (!change) return false;
      const ok = writeCell(change.sheet, change.row, change.col, change.valueAfter, false);
      if (!ok) return false;
      aiChangesRef.current.delete(id);
      disposePopover(id);
      notifyAIChange();
      return true;
    },
    [disposePopover, notifyAIChange, writeCell],
  );
  const rejectCellAtImpl = useCallback(
    (id: string): boolean => {
      const change = aiChangesRef.current.get(id);
      if (!change) return false;
      const ok = writeCell(change.sheet, change.row, change.col, change.valueBefore, false);
      if (!ok) return false;
      aiChangesRef.current.delete(id);
      disposePopover(id);
      notifyAIChange();
      return true;
    },
    [disposePopover, notifyAIChange, writeCell],
  );

  useImperativeHandle(
    ref,
    () => ({
      flush,
      applyAIProposal(proposal) {
        const api = univerRef.current?.univerAPI;
        if (!api) {
          console.warn(
            "[XlsxEditorInline] applyAIProposal called before Univer ready",
          );
          return false;
        }
        if (proposal.kind !== "xlsx_set_cell") {
          console.warn(
            `[XlsxEditorInline] unsupported proposal kind: ${proposal.kind}`,
          );
          return false;
        }
        const payload = proposal.payload as {
          sheet?: string;
          row?: number;
          col?: number;
          value?: string | number | boolean | null;
        } | null;
        if (
          !payload ||
          typeof payload.row !== "number" ||
          typeof payload.col !== "number"
        ) {
          console.warn(
            "[XlsxEditorInline] xlsx_set_cell payload missing row/col",
          );
          return false;
        }
        const workbook = api.getActiveWorkbook();
        if (!workbook) return false;
        const sheet =
          (payload.sheet && workbook.getSheetByName(payload.sheet)) ||
          workbook.getActiveSheet();
        if (!sheet) return false;
        // Capture the value BEFORE we overwrite — drives reject restore.
        const sheetName =
          payload.sheet ?? sheet.getSheetName?.() ?? "Sheet1";
        const valueBefore = readCellValue(api, payload.sheet, payload.row, payload.col);
        const ok = writeCell(payload.sheet, payload.row, payload.col, payload.value ?? null, true);
        if (!ok) return false;
        const id = cellKey(sheetName, payload.row, payload.col, proposal.id ?? null);
        const change: XlsxAIChange = {
          id,
          sheet: sheetName,
          row: payload.row,
          col: payload.col,
          valueBefore,
          valueAfter: payload.value ?? null,
          proposalId: proposal.id ?? null,
        };
        aiChangesRef.current.set(id, change);
        // LATERAL-RAIL ONLY (Jun 24, 2026): we DELIBERATELY do NOT attach an
        // inline per-cell canvas-popover here. Many simultaneous proposals made
        // the sheet a "gran casino" of overlapping floating cards. Like Word's
        // redline (in-doc highlight + side panel), xlsx now keeps the orange
        // cell highlight (written above) for in-place visibility and routes the
        // Accetta/Rifiuta + rationale affordance EXCLUSIVELY to the lateral
        // AISummaryPanel rail (fed by ``aiChangesRef`` via ``listAIChanges``).
        notifyAIChange();
        return true;
      },
      listAIChanges() {
        return Array.from(aiChangesRef.current.values());
      },
      acceptCellAt: acceptCellAtImpl,
      rejectCellAt: rejectCellAtImpl,
      acceptAllAICells() {
        let any = false;
        for (const change of Array.from(aiChangesRef.current.values())) {
          if (acceptCellAtImpl(change.id)) any = true;
        }
        return any;
      },
      rejectAllAICells() {
        let any = false;
        // Restore in REVERSE insertion order so the most recent overwrite
        // is undone first — keeps multi-write-to-same-cell scenarios
        // consistent (rare but cheap to do right).
        const entries = Array.from(aiChangesRef.current.values()).reverse();
        for (const change of entries) {
          if (rejectCellAtImpl(change.id)) any = true;
        }
        return any;
      },
      scrollToAICell(id) {
        const change = aiChangesRef.current.get(id);
        if (!change) return;
        const api = univerRef.current?.univerAPI;
        if (!api) return;
        const workbook = api.getActiveWorkbook();
        if (!workbook) return;
        const sheet =
          workbook.getSheetByName(change.sheet) ?? workbook.getActiveSheet();
        if (!sheet) return;
        // Univer ranges don't expose scrollIntoView at the facade level.
        // Setting the active selection auto-scrolls the viewport — that's
        // the documented escape hatch (May 2026 Univer docs).
        try {
          const range = sheet.getRange(change.row, change.col);
          sheet.setActiveRange?.(range);
        } catch {
          /* facade variance — best effort */
        }
      },
      subscribeAIChanges(listener) {
        aiListenersRef.current.add(listener);
        return () => {
          aiListenersRef.current.delete(listener);
        };
      },
      getSelectedPath() {
        const api = univerRef.current?.univerAPI;
        if (!api) return null;
        try {
          const workbook = api.getActiveWorkbook();
          const sheet = workbook?.getActiveSheet();
          if (!sheet) return null;
          // Univer facade variance across versions — try the documented
          // selection accessors in order and bail (null) if none resolve.
          const selection =
            (sheet as { getSelection?: () => unknown }).getSelection?.() ??
            (workbook as { getSelection?: () => unknown })?.getSelection?.();
          const range =
            (selection as { getActiveRange?: () => unknown } | null)?.getActiveRange?.() ??
            (sheet as { getActiveRange?: () => unknown }).getActiveRange?.();
          if (!range) return null;
          const r = range as {
            getRow?: () => number;
            getColumn?: () => number;
            getRange?: () => { startRow?: number; startColumn?: number };
          };
          const startRow = r.getRow?.() ?? r.getRange?.()?.startRow;
          const startCol = r.getColumn?.() ?? r.getRange?.()?.startColumn;
          if (typeof startRow !== "number" || typeof startCol !== "number") return null;
          const sheetName = sheet.getSheetName?.() ?? "Sheet1";
          // SheetJS canonical A1 encoder (0-based r/c → "B5") — same helper
          // used elsewhere in this file (snapshot + cell-label), DRY.
          return `/${sheetName}/${XLSX.utils.encode_cell({ r: startRow, c: startCol })}`;
        } catch {
          return null;
        }
      },
    }),
    [cellKey, flush, notifyAIChange, readCellValue, writeCell],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Chrome reduction (May 26, 2026 — sibling fix to ``DocxEditorInline``):
  // we no longer surface the filename inside the editor TitleBar — the
  // outer ``FocusedViewLayout`` eyebrow + ``<h1>`` already carries it.
  // The thin row keeps the editing-state pill + Salva CTA and adds a
  // persistent "Scrivi qui o chiedi ad Ando" hint to communicate the
  // dual editing model the moment the workbook mounts. This mirrors the
  // docx affordance pattern so the cross-mime UX stays consistent.
  return (
    <div className="flex h-full w-full flex-col bg-cream">
      <div className="flex h-9 items-center justify-end gap-2 border-hairline border-b px-3">
        {dirty ? (
          <span className="text-[11px] text-ando-orange" aria-live="polite">
            {i18n._(msg`Modifiche non salvate`)}
          </span>
        ) : (
          <span className="hidden text-[11px] text-ink-3 lg:inline">
            {i18n._(msg`Scrivi qui o chiedi ad Ando`)}
          </span>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() => void flush()}
          disabled={saving || !ready}
        >
          {saving ? i18n._(msg`Salvataggio…`) : i18n._(msg`Salva`)}
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        {error ? (
          <div className="px-6 py-10 text-ink-3 text-sm">
            {i18n._(msg`Impossibile inizializzare il foglio.`)} ({error.message})
          </div>
        ) : null}
        <div ref={hostRef} className="h-full w-full" />
      </div>
    </div>
  );
});
